import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import customizationApi from '../../utils/customizationApi';

const PG_TYPES = ['varchar', 'text', 'integer', 'bigint', 'numeric', 'boolean', 'date', 'timestamptz', 'uuid', 'jsonb'];

const MODULE_LABELS = {
  contacts: 'Contacts',
  accounts: 'Accounts',
  job_requirements: 'Job requirements',
  documents: 'Documents',
  timesheets: 'Timesheets',
  pto_requests: 'PTO requests',
  tickets: 'Tickets'
};

const CustomFieldsAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [gate, setGate] = useState(null);
  const [moduleCode, setModuleCode] = useState('contacts');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    field_key: '',
    label: '',
    pg_type: 'varchar',
    max_length: 255,
    is_required: false,
    visible_in_module: true,
    display_order: 0
  });

  const allowedModules = gate?.knownModules?.filter((m) => gate.modules?.[m.code]) || [];

  const loadGate = async () => {
    const r = await customizationApi.get('/v1/admin/ui-gate');
    setGate(r.data?.data || null);
  };

  const loadFields = async () => {
    const r = await customizationApi.get('/v1/admin/fields', { params: { module_code: moduleCode } });
    setRows(r.data?.data?.rows || []);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      await loadGate();
    } catch {
      setGate(null);
    }
    try {
      await loadFields();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load fields');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- moduleCode
  }, [moduleCode]);

  const saveNew = async () => {
    try {
      await customizationApi.post('/v1/admin/fields', {
        module_code: moduleCode,
        ...form,
        max_length: form.pg_type === 'varchar' ? form.max_length : null
      });
      toast.success('Field created');
      setOpen(false);
      setForm({
        field_key: '',
        label: '',
        pg_type: 'varchar',
        max_length: 255,
        is_required: false,
        visible_in_module: true,
        display_order: 0
      });
      loadFields();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Create failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this field definition?')) return;
    try {
      await customizationApi.delete(`/v1/admin/fields/${id}`);
      toast.success('Deleted');
      loadFields();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  if (!gate?.showCustomizationMenus) {
    return (
      <Box p={2}>
        <Alert severity="info">
          Enable customization at the application level and for at least one module under{' '}
          <strong>Administrator → Customize application settings</strong> before adding fields.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Add fields
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Define PostgreSQL-backed custom columns (metadata only in this release). Allowed modules are controlled in
        Customize application settings.
      </Typography>

      <Box sx={{ mb: 2, maxWidth: 320 }}>
        <TextField
          select
          fullWidth
          label="Module"
          value={moduleCode}
          onChange={(e) => setModuleCode(e.target.value)}
        >
          {allowedModules.length === 0 ? (
            <MenuItem value="">No modules enabled</MenuItem>
          ) : (
            allowedModules.map((m) => (
              <MenuItem key={m.code} value={m.code}>
                {m.label || MODULE_LABELS[m.code] || m.code}
              </MenuItem>
            ))
          )}
        </TextField>
      </Box>

      <Button startIcon={<AddIcon />} variant="contained" sx={{ mb: 2 }} onClick={() => setOpen(true)} disabled={!allowedModules.length}>
        Add field
      </Button>

      {loading ? (
        <CircularProgress />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Key</TableCell>
              <TableCell>Label</TableCell>
              <TableCell>PG type</TableCell>
              <TableCell>Max len</TableCell>
              <TableCell>Required</TableCell>
              <TableCell>Visible</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.field_key}</TableCell>
                <TableCell>{r.label}</TableCell>
                <TableCell>{r.pg_type}</TableCell>
                <TableCell>{r.max_length ?? '—'}</TableCell>
                <TableCell>{r.is_required ? 'Yes' : 'No'}</TableCell>
                <TableCell>{r.visible_in_module ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <Button size="small" color="error" onClick={() => remove(r.id)}>
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New custom field</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Field key (snake_case)"
            value={form.field_key}
            onChange={(e) => setForm((f) => ({ ...f, field_key: e.target.value }))}
            helperText="PostgreSQL-safe identifier, e.g. external_ref_id"
          />
          <TextField
            label="Label"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          />
          <TextField select label="PostgreSQL type" value={form.pg_type} onChange={(e) => setForm((f) => ({ ...f, pg_type: e.target.value }))}>
            {PG_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          {form.pg_type === 'varchar' && (
            <TextField
              type="number"
              label="Max length"
              value={form.max_length}
              onChange={(e) => setForm((f) => ({ ...f, max_length: parseInt(e.target.value, 10) || 1 }))}
            />
          )}
          <FormControlLabel
            control={<Switch checked={form.is_required} onChange={(e) => setForm((f) => ({ ...f, is_required: e.target.checked }))} />}
            label="Mandatory"
          />
          <FormControlLabel
            control={
              <Switch checked={form.visible_in_module} onChange={(e) => setForm((f) => ({ ...f, visible_in_module: e.target.checked }))} />
            }
            label="Visible on module UI (when implemented)"
          />
          <TextField
            type="number"
            label="Display order"
            value={form.display_order}
            onChange={(e) => setForm((f) => ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveNew}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomFieldsAdmin;
